import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Building2, LifeBuoy, Loader2, Mail, Settings2, ShieldAlert, UserRoundCog } from 'lucide-react';
import ConfirmationModal from '../ui/ConfirmationModal';
import WhiteLabelDeploymentsSection from './whiteLabel/WhiteLabelDeploymentsSection';
import OnboardingTourSection from './admin/OnboardingTourSection';
import SupportTicketsSection from './admin/SupportTicketsSection';
import PartnerPayoutsSection from './admin/PartnerPayoutsSection';
import FoundingPartnerOutreachSection from './admin/FoundingPartnerOutreachSection';
import EmailPortalSection from './admin/EmailPortalSection';
import BetaFeedbackSection from './admin/BetaFeedbackSection';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  fetchAdminAssetClassAvailability,
  saveAdminAssetClassAvailability,
  type AdminAssetClass,
  type AdminAssetAvailabilityResolvedFrom,
  resetAdminTrial,
  type AdminAssetClassAvailabilityState,
  type AdminBillingAccessState,
  type AdminTrialResetPayload,
  type AdminTrialResetResponse,
} from '../../lib/adminTools';

type Theme = 'light' | 'dark';
type AdminToolsSection = 'support' | 'access' | 'email' | 'partners' | 'platform';

const ADMIN_TOOLS_SECTIONS: Array<{ id: AdminToolsSection; label: string; icon: typeof LifeBuoy }> = [
  { id: 'support', label: 'Support', icon: LifeBuoy },
  { id: 'access', label: 'User Access', icon: UserRoundCog },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'partners', label: 'Partners', icon: Building2 },
  { id: 'platform', label: 'Platform', icon: Settings2 },
];

type AccountIdentity = {
  uid?: string | null;
  email?: string | null;
};

type AdminToolsPageProps = {
  theme: Theme;
  accountIdentity?: AccountIdentity | null;
  accessState?: AdminBillingAccessState | null;
  accessReady?: boolean;
  accessError?: string | null;
  onRetryAccess?: () => void;
  onNavigate?: (page: any) => void;
};

const TENANT_OPTIONS = [
  { value: 'trade-proof', label: 'Trade-Proof' },
  { value: 'warrior-trading', label: 'Warrior Trading' },
] as const;

const DISPLAY_ASSET_CLASSES = ['STOCKS', 'FUTURES', 'FOREX', 'CRYPTO'] as const;
const DEFAULT_ASSET_TENANT_VALUE = 'deployment-default';
const DEFAULT_ASSET_TENANT_OPTIONS = [
  { tenantSlug: null, tenantLabel: 'Deployment default' },
  ...TENANT_OPTIONS.map((option) => ({ tenantSlug: option.value, tenantLabel: option.label })),
] as const;

function normalizeEmail(value: string) {
  return String(value || '').trim().toLowerCase();
}

function getTenantLabel(tenantSlug: string) {
  return TENANT_OPTIONS.find((option) => option.value === tenantSlug)?.label || tenantSlug;
}

function getResolvedFromLabel(resolvedFrom: AdminAssetAvailabilityResolvedFrom | undefined) {
  if (resolvedFrom === 'tenant_persistence') return 'Saved tenant settings';
  if (resolvedFrom === 'tenant_override') return 'Tenant override';
  if (resolvedFrom === 'env_fallback') return 'Env fallback';
  return 'Deployment default';
}

function sortAssetClasses(assetClasses: AdminAssetClass[]) {
  return DISPLAY_ASSET_CLASSES.filter((assetClass) => assetClasses.includes(assetClass));
}

function serializeLaunchDraft(replayEnabled: boolean, enabledAssetClasses: AdminAssetClass[], navigationLayoutMode: 'both' | 'side_only' | 'top_only') {
  return JSON.stringify({
    replayEnabled,
    enabledAssetClasses: sortAssetClasses(enabledAssetClasses),
    navigationLayoutMode,
  });
}

function summarizeResetResult(result: AdminTrialResetResponse) {
  const tenantLabel = getTenantLabel(result.tenantSlug);

  if (!result.rowFound) {
    return `No billing row was found for ${result.email} on ${tenantLabel}. Trial eligibility was already clear.`;
  }

  if (result.deleted) {
    return `Trial eligibility reset for ${result.email} on ${tenantLabel}.`;
  }

  return `Reset completed for ${result.email} on ${tenantLabel}.`;
}

export default function AdminToolsPage({
  theme,
  accountIdentity,
  accessState = null,
  accessReady = false,
  accessError = null,
  onRetryAccess,
  onNavigate,
}: AdminToolsPageProps) {
  const [activeSection, setActiveSection] = useState<AdminToolsSection>('support');
  const [tenantSlug, setTenantSlug] = useState<AdminTrialResetPayload['tenantSlug'] | ''>('');
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assetAvailabilityTenantValue, setAssetAvailabilityTenantValue] = useState(DEFAULT_ASSET_TENANT_VALUE);
  const [assetAvailabilityState, setAssetAvailabilityState] = useState<AdminAssetClassAvailabilityState | null>(null);
  const [assetAvailabilityReady, setAssetAvailabilityReady] = useState(false);
  const [assetAvailabilityError, setAssetAvailabilityError] = useState<string | null>(null);
  const [draftReplayEnabled, setDraftReplayEnabled] = useState(true);
  const [draftEnabledAssetClasses, setDraftEnabledAssetClasses] = useState<AdminAssetClass[]>(['STOCKS']);
  const [draftNavigationLayoutMode, setDraftNavigationLayoutMode] = useState<'both' | 'side_only' | 'top_only'>('both');
  const [savedDraftSignature, setSavedDraftSignature] = useState('');
  const [assetAvailabilitySaving, setAssetAvailabilitySaving] = useState(false);
  const [assetAvailabilitySaveMessage, setAssetAvailabilitySaveMessage] = useState<string | null>(null);

  const isDark = theme === 'dark';
  const isAdminAllowed = Boolean(accessState?.canAccessAdminTab || accessState?.isAdmin);
  const resetTrialAllowed = Boolean(accessState?.tools?.resetTrial);
  const panelClassName = isDark
    ? 'rounded-[28px] border border-slate-800/70 bg-[#0b0f14] text-white shadow-[0_24px_90px_rgba(0,0,0,0.42),inset_0_1px_0_rgba(255,255,255,0.03)]'
    : 'rounded-[28px] border border-zinc-200/80 bg-white text-zinc-900 shadow-[0_20px_50px_rgba(15,23,42,0.08)]';
  const sectionClassName = isDark
    ? 'rounded-xl border border-slate-800/70 bg-[#11151c] p-5 shadow-[0_10px_24px_rgba(2,6,23,0.35)]'
    : 'rounded-xl border border-[#d6d6d6] bg-white p-5 shadow-[0_8px_20px_rgba(15,23,42,0.05)]';
  const fieldClassName = isDark
    ? 'mt-2 h-10 rounded-xl border-slate-700 !bg-slate-950 text-slate-100 placeholder:text-slate-500'
    : 'mt-2 h-10 rounded-xl border-zinc-200 bg-white text-zinc-900 placeholder:text-zinc-400';
  const selectClassName = isDark
    ? 'admin-tools-select mt-2 h-10 w-full appearance-none rounded-xl border border-slate-700 bg-slate-950 pl-3 pr-10 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500'
    : 'admin-tools-select mt-2 h-11 w-full appearance-none rounded-xl border border-zinc-200 bg-white pl-3 pr-10 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-blue-500';
  const subtleTextClassName = isDark ? 'text-slate-400' : 'text-zinc-500';
  // Shared admin button style families: primary admin actions and secondary admin
  // actions must each use one consistent visual treatment (matching the Reset Trial
  // and Reset Tour Progress buttons respectively).
  const adminPrimaryBtnClass = `h-10 rounded-xl px-5 ${isDark ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'border border-rose-500/30 bg-rose-500/10 text-rose-700 hover:bg-rose-500/15'}`;
  const adminSecondaryBtnClass = 'h-10 rounded-xl border border-slate-700 px-5';

  const confirmationSummary = useMemo(() => {
    if (!tenantSlug || !email) return null;
    return `${normalizeEmail(email)} · ${getTenantLabel(tenantSlug)}`;
  }, [email, tenantSlug]);

  const assetAvailabilityTenantOptions = useMemo(
    () => (assetAvailabilityState?.tenantOptions?.length ? assetAvailabilityState.tenantOptions : DEFAULT_ASSET_TENANT_OPTIONS),
    [assetAvailabilityState?.tenantOptions],
  );

  const assetAvailabilityResolvedFromLabel = getResolvedFromLabel(assetAvailabilityState?.resolvedFrom);
  const assetAvailabilityContextLabel = assetAvailabilityState?.tenantLabel || 'Deployment default';
  const assetAvailabilityContextSlug = assetAvailabilityState?.tenantSlug || 'deployment-default';
  const draftSignature = serializeLaunchDraft(draftReplayEnabled, draftEnabledAssetClasses, draftNavigationLayoutMode);
  const assetAvailabilityDirty = Boolean(assetAvailabilityState?.writable && savedDraftSignature && draftSignature !== savedDraftSignature);
  const canSaveAssetAvailability = Boolean(
    assetAvailabilityState?.writable
    && assetAvailabilityDirty
    && !assetAvailabilitySaving
    && assetAvailabilityReady
    && draftEnabledAssetClasses.length > 0,
  );

  const applyAssetAvailabilityState = (nextState: AdminAssetClassAvailabilityState) => {
    const enabled = sortAssetClasses(nextState.enabledAssetClasses || []);
    const layoutMode = nextState.navigationLayoutMode || 'both';
    setAssetAvailabilityState(nextState);
    setDraftReplayEnabled(Boolean(nextState.replayEnabled));
    setDraftEnabledAssetClasses(enabled.length ? enabled : ['STOCKS']);
    setDraftNavigationLayoutMode(layoutMode);
    setSavedDraftSignature(serializeLaunchDraft(Boolean(nextState.replayEnabled), enabled.length ? enabled : ['STOCKS'], layoutMode));
  };

  useEffect(() => {
    let cancelled = false;

    if (!accessReady) {
      setAssetAvailabilityReady(false);
      return () => {
        cancelled = true;
      };
    }

    if (!isAdminAllowed) {
      setAssetAvailabilityState(null);
      setAssetAvailabilityError(null);
      setAssetAvailabilityReady(true);
      return () => {
        cancelled = true;
      };
    }

    setAssetAvailabilityReady(false);
    setAssetAvailabilityError(null);

    void (async () => {
      try {
        const nextState = await fetchAdminAssetClassAvailability(
          accountIdentity?.uid ? { uid: String(accountIdentity.uid), email: accountIdentity.email || null } : null,
          { tenantSlug: assetAvailabilityTenantValue === DEFAULT_ASSET_TENANT_VALUE ? null : assetAvailabilityTenantValue },
        );
        if (cancelled) return;
        applyAssetAvailabilityState(nextState);
      } catch (error: any) {
        if (cancelled) return;
        setAssetAvailabilityState(null);
        setSavedDraftSignature('');
        setAssetAvailabilityError(String(error?.message || 'Unable to load launch controls.').trim() || 'Unable to load launch controls.');
      } finally {
        if (!cancelled) {
          setAssetAvailabilityReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessReady, isAdminAllowed, accountIdentity?.uid, accountIdentity?.email, assetAvailabilityTenantValue]);

  const handleAssetAvailabilityTenantChange = (nextValue: string) => {
    if (assetAvailabilityDirty && !window.confirm('Discard unsaved launch-control changes for this tenant?')) {
      return;
    }
    setAssetAvailabilitySaveMessage(null);
    setAssetAvailabilityTenantValue(nextValue);
  };

  const toggleDraftAssetClass = (assetClass: AdminAssetClass) => {
    setAssetAvailabilitySaveMessage(null);
    setDraftEnabledAssetClasses((current) => {
      if (current.includes(assetClass)) {
        return current.filter((value) => value !== assetClass);
      }
      return sortAssetClasses([...current, assetClass]);
    });
  };

  const handleRevertAssetAvailability = () => {
    if (!assetAvailabilityState) return;
    const enabled = sortAssetClasses(assetAvailabilityState.enabledAssetClasses || []);
    setDraftReplayEnabled(Boolean(assetAvailabilityState.replayEnabled));
    setDraftEnabledAssetClasses(enabled.length ? enabled : ['STOCKS']);
    setDraftNavigationLayoutMode(assetAvailabilityState.navigationLayoutMode || 'both');
    setAssetAvailabilitySaveMessage(null);
  };

  const handleSaveAssetAvailability = async () => {
    if (!assetAvailabilityState?.tenantSlug) {
      setAssetAvailabilityError('Select a tenant before saving launch controls.');
      return;
    }
    if (!draftEnabledAssetClasses.length) {
      setAssetAvailabilityError('At least one asset class must stay enabled.');
      return;
    }

    setAssetAvailabilitySaving(true);
    setAssetAvailabilityError(null);
    setAssetAvailabilitySaveMessage(null);

    try {
      const nextState = await saveAdminAssetClassAvailability(
        accountIdentity?.uid ? { uid: String(accountIdentity.uid), email: accountIdentity.email || null } : null,
        {
          tenantSlug: assetAvailabilityState.tenantSlug,
          replayEnabled: draftReplayEnabled,
          enabledAssetClasses: sortAssetClasses(draftEnabledAssetClasses),
          navigationLayoutMode: draftNavigationLayoutMode,
        },
      );
      applyAssetAvailabilityState(nextState);
      setAssetAvailabilitySaveMessage('Launch controls saved.');
    } catch (error: any) {
      setAssetAvailabilityError(String(error?.message || 'Unable to save launch controls.').trim() || 'Unable to save launch controls.');
    } finally {
      setAssetAvailabilitySaving(false);
    }
  };

  const clearMessages = () => {
    setValidationError(null);
    setRequestError(null);
    setSuccessMessage(null);
  };

  const validateForm = () => {
    const normalizedEmail = normalizeEmail(email);
    if (!tenantSlug || !normalizedEmail) {
      setValidationError('Select a tenant and enter an email address.');
      return null;
    }

    return {
      tenantSlug,
      email: normalizedEmail,
    } as AdminTrialResetPayload;
  };

  const requestConfirmation = () => {
    clearMessages();
    const payload = validateForm();
    if (!payload) return;
    setConfirmOpen(true);
  };

  const handleConfirmReset = async () => {
    const payload = validateForm();
    if (!payload) {
      setConfirmOpen(false);
      return;
    }

    setConfirmOpen(false);
    setSubmitting(true);
    setRequestError(null);
    setSuccessMessage(null);

    try {
      const result = await resetAdminTrial(
        accountIdentity?.uid ? { uid: String(accountIdentity.uid), email: accountIdentity.email || null } : null,
        payload,
      );
      setSuccessMessage(summarizeResetResult(result));
    } catch (error: any) {
      setRequestError(String(error?.message || 'Unable to reset the trial right now.').trim() || 'Unable to reset the trial right now.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!accessReady) {
    return (
      <section className={`${panelClassName} p-8`}>
        <div className="flex items-center gap-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          <span>Checking admin access…</span>
        </div>
      </section>
    );
  }

  if (accessError) {
    return (
      <section className={`${panelClassName} p-8`}>
        <div className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100 dark:text-amber-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div className="space-y-3">
            <div>
              <p className="font-semibold text-amber-200">Admin access could not be verified.</p>
              <p className={subtleTextClassName}>{accessError}</p>
            </div>
            {onRetryAccess ? (
              <Button type="button" variant="secondary" onClick={onRetryAccess}>
                Retry access check
              </Button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  if (!isAdminAllowed) {
    return (
      <section className={`${panelClassName} p-8`}>
        <div className="flex items-start gap-4 rounded-3xl border border-rose-500/25 bg-rose-500/8 p-6">
          <ShieldAlert className="mt-1 h-5 w-5 shrink-0 text-rose-400" />
          <div>
            <h1 className="text-2xl font-semibold">Access denied</h1>
            <p className={`mt-2 text-sm ${subtleTextClassName}`}>
              This route is reserved for admin-only tools.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <main className="admin-tools-page mx-auto w-full max-w-[1600px] bg-[#f5f5f5] p-4 md:p-8 dark:bg-transparent">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          <header className="flex flex-col gap-2 md:col-span-2 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold tracking-tight">Admin Tools</h1>
              <p className={`text-sm leading-5 ${subtleTextClassName}`}>
                Manage platform operations, partner tenants, support, outreach, billing, and other administrative workflows.
              </p>
            </div>
          </header>

          <nav
            role="tablist"
            aria-label="Admin tool sections"
            className={`md:col-span-2 -mx-1 flex gap-3 overflow-x-auto px-1 pb-1 ${isDark ? 'scrollbar-thin scrollbar-thumb-slate-700' : 'scrollbar-thin scrollbar-thumb-zinc-300'}`}
          >
            {ADMIN_TOOLS_SECTIONS.map(({ id, label, icon: Icon }) => {
              const selected = activeSection === id;
              return (
                <button
                  key={id}
                  id={`admin-tools-tab-${id}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`admin-tools-panel-${id}`}
                  onClick={() => setActiveSection(id)}
                  className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected
                    ? (isDark ? 'border-blue-500/60 bg-blue-500/15 text-blue-200' : 'border-blue-500 bg-blue-50 text-blue-800')
                    : (isDark ? 'border-slate-800 bg-slate-900/70 text-slate-300 hover:border-slate-600 hover:bg-slate-800' : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50')}`}
                >
                  <Icon aria-hidden="true" className="h-4 w-4" />
                  {label}
                </button>
              );
            })}
          </nav>

          <div
            id="admin-tools-panel-support"
            role="tabpanel"
            aria-labelledby="admin-tools-tab-support"
            hidden={activeSection !== 'support'}
            className={`md:col-span-2 ${activeSection === 'support' ? 'grid grid-cols-1 gap-4 md:gap-6' : 'hidden'}`}
          >
            <SupportTicketsSection theme={theme} identity={accountIdentity} onNavigate={onNavigate} />
            <div data-testid="admin-beta-feedback-section" className="mt-5">
              <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/60">
                <BetaFeedbackSection theme={theme} />
              </div>
            </div>
          </div>

          <div
            id="admin-tools-panel-access"
            role="tabpanel"
            aria-labelledby="admin-tools-tab-access"
            hidden={activeSection !== 'access'}
            className={`md:col-span-2 ${activeSection === 'access' ? 'grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6' : 'hidden'}`}
          >
          <section className={`${sectionClassName} flex flex-col`}>
            <div className="mb-2">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">TRIAL MANAGEMENT</h2>
              </div>
              <p className={`mt-0.5 text-sm leading-5 ${subtleTextClassName}`}>
                Reset trial status for this tenant only, allowing eligibility to be rechecked safely.
              </p>
            </div>

            {!resetTrialAllowed ? (
              <div className={`mt-3 rounded-xl border px-4 py-2.5 text-sm ${isDark ? 'border-slate-700 bg-slate-900/50 text-slate-300' : 'border-zinc-200 bg-zinc-100 text-zinc-600'}`}>
                Reset Trial is currently disabled for this admin role.
              </div>
            ) : (
              <>
                <form
                  className="mt-auto grid gap-2.5 pt-3 md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto] md:items-end"
                  onSubmit={(event) => {
                    event.preventDefault();
                    requestConfirmation();
                  }}
                >
              <label className="block text-sm font-medium">
                Tenant
                <select
                  aria-label="Tenant"
                  value={tenantSlug}
                  onChange={(event) => {
                    setTenantSlug(event.target.value as AdminTrialResetPayload['tenantSlug'] | '');
                    clearMessages();
                  }}
                  className={selectClassName}
                >
                  <option value="">Select tenant</option>
                  {TENANT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm font-medium">
                Email
                <Input
                  aria-label="Email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    clearMessages();
                  }}
                  className={fieldClassName}
                />
              </label>

              <Button type="submit" className={adminPrimaryBtnClass} disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Resetting…
                  </>
                ) : 'Reset Trial'}
              </Button>
                </form>

                {validationError ? (
                  <div className="mt-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-100 dark:text-amber-100">
                    {validationError}
                  </div>
                ) : null}

                {requestError ? (
                  <div className="mt-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-100 dark:text-rose-100">
                    {requestError}
                  </div>
                ) : null}

                {successMessage ? (
                  <div className="mt-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-100 dark:text-emerald-100">
                    {successMessage}
                  </div>
                ) : null}
              </>
            )}
          </section>

          <OnboardingTourSection theme={theme} identity={accountIdentity} />
          </div>

          <div
            id="admin-tools-panel-email"
            role="tabpanel"
            aria-labelledby="admin-tools-tab-email"
            hidden={activeSection !== 'email'}
            className={`md:col-span-2 min-w-0 ${activeSection === 'email' ? '' : 'hidden'}`}
          >
          <EmailPortalSection theme={theme} identity={accountIdentity} />
          </div>

          <div
            id="admin-tools-panel-partners"
            role="tabpanel"
            aria-labelledby="admin-tools-tab-partners"
            hidden={activeSection !== 'partners'}
            className={`md:col-span-2 ${activeSection === 'partners' ? 'grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6' : 'hidden'}`}
          >
          <div className="md:col-span-2">
            <WhiteLabelDeploymentsSection theme={theme} />
          </div>

          <FoundingPartnerOutreachSection theme={theme} identity={accountIdentity} />
          <section className={`${sectionClassName} md:col-span-2`} data-testid="admin-partner-payouts">
            <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">PARTNER PAYOUTS</h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              Record actual payments against frozen payout records.
            </p>
            <div className="mt-4">
              <PartnerPayoutsSection accountIdentity={accountIdentity} />
            </div>
          </section>
          </div>

          <div
            id="admin-tools-panel-platform"
            role="tabpanel"
            aria-labelledby="admin-tools-tab-platform"
            hidden={activeSection !== 'platform'}
            className={`md:col-span-2 ${activeSection === 'platform' ? 'grid grid-cols-1 gap-4 md:gap-6' : 'hidden'}`}
          >
          <section className={`${sectionClassName} md:col-span-2`}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">LAUNCH CONTROLS</h2>
                <p className={`text-sm leading-5 ${subtleTextClassName}`}>
                  Toggle asset classes and "Replay Chart" ON/OFF per tenant.
                </p>
              </div>
            </div>

            {!assetAvailabilityReady ? (
              <div className={`mt-4 flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm ${isDark ? 'border-slate-800 bg-slate-900/40 text-slate-300' : 'border-zinc-200 bg-white text-zinc-700'}`}>
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                <span>Loading resolved launch controls…</span>
              </div>
            ) : assetAvailabilityError ? (
              <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 dark:text-amber-100">
                {assetAvailabilityError}
              </div>
            ) : assetAvailabilityState ? (
              <>
                <div className={`mt-3 flex flex-col gap-2 rounded-lg border px-3 py-2 text-xs md:flex-row md:items-center md:justify-between ${isDark ? 'border-slate-800 bg-slate-900/40 text-slate-300' : 'border-zinc-200 bg-white text-zinc-600'}`}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className={`font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-400' : 'text-zinc-500'}`}>
                      Resolved from: {assetAvailabilityResolvedFromLabel}
                    </span>
                    <span>Tenant: {assetAvailabilityContextLabel}</span>
                    <span>Slug/context: {assetAvailabilityContextSlug}</span>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-medium">
                    Tenant
                    <select
                      aria-label="Tenant"
                      value={assetAvailabilityTenantValue}
                      onChange={(event) => {
                        handleAssetAvailabilityTenantChange(event.target.value);
                      }}
                      className={`${selectClassName} !mt-0 h-8 w-auto min-w-[11rem] rounded-lg pr-20`}
                      disabled={assetAvailabilitySaving}
                    >
                      {assetAvailabilityTenantOptions.map((option) => {
                        const optionValue = option.tenantSlug || DEFAULT_ASSET_TENANT_VALUE;
                        return (
                          <option key={optionValue} value={optionValue}>
                            {option.tenantLabel}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-5">
                  {DISPLAY_ASSET_CLASSES.map((assetClass) => {
                    const enabled = draftEnabledAssetClasses.includes(assetClass);
                    const disabled = !assetAvailabilityState.writable || assetAvailabilitySaving || !assetAvailabilityReady;
                    return (
                    <div
                      key={assetClass}
                      className={`flex items-center justify-between rounded-lg border px-2.5 py-2 ${isDark ? 'border-slate-800 bg-slate-900/40' : 'border-zinc-200 bg-white'}`}
                    >
                      <div className="text-sm font-semibold tracking-wide">{assetClass}</div>
                      <button
                        type="button"
                        aria-label={`${assetClass} availability toggle`}
                        aria-pressed={enabled}
                        disabled={disabled}
                        onClick={() => toggleDraftAssetClass(assetClass)}
                        className={`inline-flex min-w-[52px] items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${enabled
                          ? (isDark ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-inset ring-emerald-500/40' : 'bg-emerald-500 text-white')
                          : (isDark ? 'bg-slate-800 text-slate-400 ring-1 ring-inset ring-slate-700' : 'bg-zinc-100 text-zinc-500 ring-1 ring-inset ring-zinc-300')}`}
                      >
                        {enabled ? 'ON' : 'OFF'}
                      </button>
                    </div>
                    );
                  })}

                  {/* Replay Chart shares the same row as the asset classes on desktop */}
                  <div
                    className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 ${isDark ? 'border-slate-800 bg-slate-900/40' : 'border-zinc-200 bg-white'}`}
                    data-testid="replay-chart-control"
                  >
                    <div className="text-sm font-semibold tracking-wide">REPLAY</div>
                    <button
                      type="button"
                      aria-label="Replay Chart availability toggle"
                      aria-pressed={draftReplayEnabled}
                      disabled={!assetAvailabilityState.writable || assetAvailabilitySaving || !assetAvailabilityReady}
                      onClick={() => {
                        setAssetAvailabilitySaveMessage(null);
                        setDraftReplayEnabled((current) => !current);
                      }}
                      className={`inline-flex min-w-[52px] items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors disabled:cursor-not-allowed disabled:opacity-70 ${draftReplayEnabled
                        ? (isDark ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-inset ring-emerald-500/40' : 'bg-emerald-500 text-white')
                        : (isDark ? 'bg-slate-800 text-slate-400 ring-1 ring-inset ring-slate-700' : 'bg-zinc-100 text-zinc-500 ring-1 ring-inset ring-zinc-300')}`}
                    >
                      {draftReplayEnabled ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </div>

                {draftEnabledAssetClasses.length === 0 ? (
                  <div className="mt-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 dark:text-amber-100">
                    At least one asset class must stay enabled.
                  </div>
                ) : null}

                <div className={`mt-4 flex flex-col gap-2 border-t pt-3 md:flex-row md:items-center md:justify-between ${isDark ? 'border-slate-800' : 'border-zinc-200'}`}>
                  <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:gap-3">
                    <div className="text-sm font-semibold tracking-wide">Navigation layout</div>
                    <select
                      aria-label="Navigation layout"
                      value={draftNavigationLayoutMode}
                      onChange={(event) => {
                        setAssetAvailabilitySaveMessage(null);
                        setDraftNavigationLayoutMode(event.target.value as 'both' | 'side_only' | 'top_only');
                      }}
                      className={`${selectClassName} !mt-0 h-8 w-full rounded-lg md:w-56`}
                      disabled={!assetAvailabilityState.writable || assetAvailabilitySaving || !assetAvailabilityReady}
                    >
                      <option value="both">Both menus</option>
                      <option value="side_only">Side menu only</option>
                      <option value="top_only">Top menu only</option>
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {assetAvailabilitySaveMessage ? (
                      <span className="mr-auto text-sm text-emerald-300 md:mr-0">{assetAvailabilitySaveMessage}</span>
                    ) : null}
                    <Button
                      type="button"
                      variant="secondary"
                      className={adminSecondaryBtnClass}
                      onClick={handleRevertAssetAvailability}
                      disabled={!assetAvailabilityDirty || assetAvailabilitySaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      className={adminPrimaryBtnClass}
                      onClick={() => {
                        void handleSaveAssetAvailability();
                      }}
                      disabled={!canSaveAssetAvailability}
                    >
                      {assetAvailabilitySaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${isDark ? 'border-slate-800 bg-slate-900/40 text-slate-300' : 'border-zinc-200 bg-white text-zinc-700'}`}>
                No launch-control status is currently available.
              </div>
            )}
          </section>
          </div>
        </div>
      </main>

      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          void handleConfirmReset();
        }}
        title="Confirm trial reset"
        message={confirmationSummary
          ? `Reset trial eligibility for this user and tenant?\n\n${confirmationSummary}`
          : 'Reset trial eligibility for this user and tenant?'}
        confirmText="Reset Trial"
        cancelText="Cancel"
      />
    </>
  );
}
